import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Avatar, Button, Spinner, Textarea } from '@nextui-org/react';
import {
  ArrowLeft,
  ChevronDown,
  ImageOff,
  MessageCircle,
  Reply,
  Send,
  Star,
  Target,
  Trash2,
  User as UserIcon,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import communityService, {
  PublicDartSetup,
  SetupComment,
} from '../services/communityService';
import setupRatingService, {
  RatingSummary,
  SetupRating,
} from '../services/setupRatingService';

const ACCENT = '#3fced1';

const SetupDetailPage: React.FC = () => {
  const { setupId } = useParams<{ setupId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState<PublicDartSetup | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [comments, setComments] = useState<SetupComment[]>([]);
  const [totalComments, setTotalComments] = useState(0);
  const [totalAllComments, setTotalAllComments] = useState(0);
  const [loadingComments, setLoadingComments] = useState(true);

  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState<SetupComment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  // Zusätzlich nachgeladene Antworten pro Kommentar-ID
  const [extraReplies, setExtraReplies] = useState<Record<string, SetupComment[]>>({});
  const [loadingRepliesFor, setLoadingRepliesFor] = useState<string | null>(null);

  // ---------- Bewertungen ----------
  const [ratingSummary, setRatingSummary] = useState<RatingSummary>({ average: 0, count: 0 });
  const [ratings, setRatings] = useState<SetupRating[]>([]);
  const [myRating, setMyRating] = useState<SetupRating | null>(null);
  const [loadingRatings, setLoadingRatings] = useState(true);
  const [ratingStars, setRatingStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingFormOpen, setRatingFormOpen] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [deletingRating, setDeletingRating] = useState(false);
  const [ratingError, setRatingError] = useState<string | null>(null);

  const loadSetup = useCallback(async () => {
    if (!setupId) return;
    setLoading(true);
    setNotFound(false);
    const result = await communityService.getSetupById(setupId);
    if (!result) {
      setNotFound(true);
    }
    setSetup(result);
    setLoading(false);
  }, [setupId]);

  const loadComments = useCallback(async () => {
    if (!setupId) return;
    setLoadingComments(true);
    const result = await communityService.getSetupComments(setupId, { limit: 50 });
    setComments(result.comments);
    setTotalComments(result.total);
    setTotalAllComments(result.totalAll);
    setLoadingComments(false);
  }, [setupId]);

  const loadRatings = useCallback(async () => {
    if (!setupId) return;
    setLoadingRatings(true);
    const [summary, list] = await Promise.all([
      setupRatingService.getSummary(setupId),
      setupRatingService.getRatings(setupId),
    ]);
    setRatingSummary(summary);
    setRatings(list);
    setLoadingRatings(false);
  }, [setupId]);

  const loadMyRating = useCallback(async () => {
    if (!setupId || !isAuthenticated) {
      setMyRating(null);
      return;
    }
    const mine = await setupRatingService.getMyRating(setupId);
    setMyRating(mine);
    if (mine) {
      setRatingStars(Math.round(mine.stars));
      setRatingComment(mine.comment ?? '');
    }
  }, [setupId, isAuthenticated]);

  useEffect(() => {
    loadSetup();
    loadComments();
    loadRatings();
    window.scrollTo({ top: 0 });
  }, [loadSetup, loadComments, loadRatings]);

  useEffect(() => {
    loadMyRating();
  }, [loadMyRating]);

  const submitRating = async () => {
    if (!setupId || ratingStars < 1) return;
    setSubmittingRating(true);
    setRatingError(null);
    try {
      await setupRatingService.submitRating(setupId, ratingStars, ratingComment);
      setRatingFormOpen(false);
      await Promise.all([loadRatings(), loadMyRating()]);
    } catch (err: any) {
      setRatingError(
        setupRatingService.extractErrorMessage(
          err,
          'Bewertung konnte nicht gespeichert werden.'
        )
      );
    } finally {
      setSubmittingRating(false);
    }
  };

  const removeMyRating = async () => {
    if (!myRating?.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Deine Bewertung wirklich löschen?')) return;
    setDeletingRating(true);
    setRatingError(null);
    try {
      await setupRatingService.deleteRating(myRating.id);
      setMyRating(null);
      setRatingStars(0);
      setRatingComment('');
      setRatingFormOpen(false);
      await loadRatings();
    } catch (err: any) {
      setRatingError(
        setupRatingService.extractErrorMessage(err, 'Bewertung konnte nicht gelöscht werden.')
      );
    } finally {
      setDeletingRating(false);
    }
  };

  const submitComment = async () => {
    if (!setupId) return;
    const content = commentText.trim();
    if (!content) return;
    setIsSubmitting(true);
    setCommentError(null);
    try {
      await communityService.addSetupComment(setupId, content, replyTo?.id ?? null);
      setCommentText('');
      setReplyTo(null);
      setExtraReplies({});
      await loadComments();
    } catch (err: any) {
      setCommentError(
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
          err?.response?.data?.message ||
          'Kommentar konnte nicht gespeichert werden.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteComment = async (comment: SetupComment) => {
    if (!setupId || !comment.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Kommentar wirklich löschen?')) return;
    setDeletingCommentId(comment.id);
    setCommentError(null);
    try {
      await communityService.deleteSetupComment(setupId, comment.id);
      setExtraReplies({});
      await loadComments();
    } catch (err: any) {
      setCommentError('Kommentar konnte nicht gelöscht werden.');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const loadMoreReplies = async (comment: SetupComment) => {
    if (!comment.id || loadingRepliesFor) return;
    setLoadingRepliesFor(comment.id);
    const result = await communityService.getCommentReplies(comment.id, { limit: 50 });
    setExtraReplies((prev) => ({ ...prev, [comment.id]: result.replies }));
    setLoadingRepliesFor(null);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (notFound || !setup) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Target className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Setup nicht gefunden</h2>
          <p className="text-zinc-400 mb-6 text-sm">
            Das gesuchte Setup existiert nicht oder ist nicht mehr öffentlich.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
        </div>
      </div>
    );
  }

  const imageUrl = communityService.buildImageUrl(setup.image);
  const userAvatarUrl = communityService.buildThumbnailUrl(setup.user?.image, 100);
  const createdAtLabel = communityService.formatRelativeTime(setup.createdAt);
  const commentCount = setup.commentCount ?? totalAllComments;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Helmet>
        <title>{setup.title} - Dart-Setup | DartCloud</title>
        <meta
          name="description"
          content={`Dart-Setup "${setup.title}"${
            setup.user?.username ? ` von ${setup.user.username}` : ''
          } in der DartCloud Community ansehen.`}
        />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Zurück */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10">
          {/* Setup-Bild (großes Hero-Bild) */}
          <div className="lg:col-span-3 relative rounded-3xl overflow-hidden border border-zinc-800 bg-zinc-900 aspect-square flex items-center justify-center shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
            {imageUrl ? (
              <img src={imageUrl} alt={setup.title} className="w-full h-full object-cover" />
            ) : (
              <ImageOff size={48} className="text-zinc-600" />
            )}
            {ratingSummary.count > 0 && (
              <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-950/80 backdrop-blur-sm border border-zinc-800">
                <Star size={14} className="text-amber-400 fill-amber-400" />
                <span className="text-sm font-bold text-white">
                  {setupRatingService.formatAverage(ratingSummary.average)}
                </span>
                <span className="text-xs text-gray-400">· {ratingSummary.count}</span>
              </div>
            )}
          </div>

          {/* Setup-Infos */}
          <div className="lg:col-span-2 flex flex-col">
            <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
              {setup.title}
            </h1>
            {setup.subtitle && <p className="mt-2 text-lg text-zinc-400">{setup.subtitle}</p>}
            {setup.description && (
              <p className="mt-4 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {setup.description}
              </p>
            )}

            <div className="mt-4 flex items-center gap-3 flex-wrap text-xs text-zinc-500">
              {createdAtLabel && <span>Erstellt {createdAtLabel}</span>}
              <span className="inline-flex items-center gap-1">
                <MessageCircle className="w-3.5 h-3.5" />
                {commentCount} {commentCount === 1 ? 'Kommentar' : 'Kommentare'}
              </span>
              {ratingSummary.count > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  {setupRatingService.formatAverage(ratingSummary.average)} (
                  {ratingSummary.count}{' '}
                  {ratingSummary.count === 1 ? 'Bewertung' : 'Bewertungen'})
                </span>
              )}
            </div>

            {/* Owner-Karte (prominent) */}
            {setup.user?.username && (
              <RouterLink
                to={`/spieler/${encodeURIComponent(setup.user.username)}`}
                className="mt-6 group relative flex items-center gap-4 rounded-2xl border border-zinc-800 bg-zinc-900/70 hover:border-[#3fced1]/50 p-4 transition-all duration-300 hover:shadow-[0_12px_32px_-12px_rgba(63,206,209,0.3)] overflow-hidden"
              >
                <div
                  className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-10 pointer-events-none"
                  style={{ backgroundColor: ACCENT }}
                />
                <div className="rounded-full p-[2px]" style={{ backgroundColor: `${ACCENT}4D` }}>
                  <Avatar
                    src={userAvatarUrl ?? undefined}
                    showFallback
                    fallback={<UserIcon size={22} className="text-zinc-500" />}
                    className="h-14 w-14 bg-zinc-800"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold">
                    Setup von
                  </p>
                  <p
                    className="text-base font-bold truncate transition-colors"
                    style={{ color: ACCENT }}
                  >
                    {setup.user.username}
                  </p>
                  <p className="text-xs text-zinc-500">Zum Spielerprofil</p>
                </div>
                <ArrowLeft
                  size={16}
                  className="rotate-180 text-zinc-600 flex-shrink-0 transition-all duration-300 group-hover:text-[#3fced1] group-hover:translate-x-0.5"
                />
              </RouterLink>
            )}
          </div>
        </div>

        {/* Bewertungen */}
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-5 h-5" style={{ color: ACCENT }} />
            <h2 className="text-lg font-semibold">
              Bewertungen {ratingSummary.count > 0 && `(${ratingSummary.count})`}
            </h2>
          </div>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4 sm:p-6">
            {loadingRatings ? (
              <div className="flex justify-center py-8">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                {ratingSummary.count > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                    {/* Durchschnitt groß */}
                    <div className="flex flex-col items-center justify-center text-center">
                      <span className="text-5xl font-extrabold text-white leading-none">
                        {setupRatingService.formatAverage(ratingSummary.average)}
                      </span>
                      <div className="mt-2">
                        <StarsDisplay value={ratingSummary.average} size={20} />
                      </div>
                      <p className="mt-1.5 text-xs text-zinc-500">
                        {ratingSummary.count}{' '}
                        {ratingSummary.count === 1 ? 'Bewertung' : 'Bewertungen'}
                      </p>
                    </div>

                    {/* Verteilung 5→1 */}
                    <div className="flex flex-col gap-1.5">
                      {setupRatingService.getStarDistribution(ratings).map((row) => (
                        <div key={row.stars} className="flex items-center gap-2">
                          <span className="w-3 text-xs text-zinc-400 text-right">
                            {row.stars}
                          </span>
                          <Star size={11} className="text-amber-400 fill-amber-400 shrink-0" />
                          <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${row.percent}%`,
                                background: `linear-gradient(90deg, ${ACCENT}, #7de8ea)`,
                              }}
                            />
                          </div>
                          <span className="w-8 text-[11px] text-zinc-500 text-right">
                            {row.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-sm text-zinc-500 py-2">
                    Noch keine Bewertungen. Sei der Erste!
                  </p>
                )}

                {/* Eigene Bewertung */}
                <div className="mt-6 pt-5 border-t border-zinc-800">
                  {!isAuthenticated ? (
                    <p className="text-sm text-zinc-400">
                      <RouterLink
                        to="/login"
                        className="text-[#3fced1] hover:text-[#35b5b8] font-medium"
                      >
                        Melde dich an
                      </RouterLink>
                      , um dieses Setup zu bewerten.
                    </p>
                  ) : myRating && !ratingFormOpen ? (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-500 mb-1">Deine Bewertung</p>
                        <div className="flex items-center gap-2">
                          <StarsDisplay value={myRating.stars} size={16} />
                          {myRating.comment && (
                            <span className="text-sm text-zinc-300 truncate">
                              „{myRating.comment}“
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="bordered"
                          className="text-white border-zinc-700"
                          onPress={() => {
                            setRatingStars(Math.round(myRating.stars));
                            setRatingComment(myRating.comment ?? '');
                            setRatingFormOpen(true);
                          }}
                        >
                          Bearbeiten
                        </Button>
                        <Button
                          size="sm"
                          variant="light"
                          color="danger"
                          isLoading={deletingRating}
                          startContent={!deletingRating && <Trash2 size={14} />}
                          onPress={removeMyRating}
                        >
                          Löschen
                        </Button>
                      </div>
                    </div>
                  ) : ratingFormOpen || !myRating ? (
                    <div>
                      <p className="text-sm font-semibold text-white mb-2">
                        {myRating ? 'Bewertung bearbeiten' : 'Dieses Setup bewerten'}
                      </p>
                      {/* Interaktive Sterne */}
                      <div
                        className="flex items-center gap-1"
                        onMouseLeave={() => setHoverStars(0)}
                        role="radiogroup"
                        aria-label="Sterne-Bewertung"
                      >
                        {[1, 2, 3, 4, 5].map((n) => {
                          const active = (hoverStars || ratingStars) >= n;
                          return (
                            <button
                              key={n}
                              type="button"
                              role="radio"
                              aria-checked={ratingStars === n}
                              aria-label={`${n} ${n === 1 ? 'Stern' : 'Sterne'}`}
                              onMouseEnter={() => setHoverStars(n)}
                              onClick={() => setRatingStars(n)}
                              className="p-1 transition-transform hover:scale-110"
                            >
                              <Star
                                size={26}
                                className={
                                  active
                                    ? 'text-amber-400 fill-amber-400'
                                    : 'text-zinc-600'
                                }
                              />
                            </button>
                          );
                        })}
                        {ratingStars > 0 && (
                          <span className="ml-2 text-sm text-zinc-400">
                            {ratingStars} von 5
                          </span>
                        )}
                      </div>
                      <Textarea
                        placeholder="Optional: Was gefällt dir an diesem Setup?"
                        value={ratingComment}
                        onValueChange={setRatingComment}
                        maxLength={500}
                        minRows={2}
                        variant="bordered"
                        className="mt-3"
                        classNames={{
                          inputWrapper:
                            'bg-zinc-950 border-zinc-700 data-[hover=true]:border-zinc-600 group-data-[focus=true]:border-[#3fced1]',
                          input: 'text-white placeholder:text-gray-500',
                        }}
                      />
                      {ratingError && (
                        <p className="mt-2 text-xs text-red-400">{ratingError}</p>
                      )}
                      <div className="mt-3 flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                          isLoading={submittingRating}
                          isDisabled={ratingStars < 1}
                          onPress={submitRating}
                        >
                          {myRating ? 'Bewertung aktualisieren' : 'Bewertung abgeben'}
                        </Button>
                        {myRating && (
                          <Button
                            size="sm"
                            variant="light"
                            className="text-gray-300"
                            onPress={() => setRatingFormOpen(false)}
                          >
                            Abbrechen
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : null}
                  {ratingError && !ratingFormOpen && (
                    <p className="mt-2 text-xs text-red-400">{ratingError}</p>
                  )}
                </div>

                {/* Bewertungs-Liste */}
                {ratings.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-zinc-800 flex flex-col gap-3">
                    {ratings.map((rating, idx) => (
                      <RatingRow key={rating.id ?? idx} rating={rating} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Kommentare */}
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-5 h-5" style={{ color: ACCENT }} />
            <h2 className="text-lg font-semibold">
              Kommentare {totalComments > 0 && `(${totalAllComments})`}
            </h2>
          </div>

          {/* Kommentar schreiben */}
          {isAuthenticated ? (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4 mb-6">
              {replyTo && (
                <div className="flex items-center justify-between gap-2 mb-2 rounded-lg bg-zinc-800/70 px-3 py-2 text-xs text-zinc-300">
                  <span className="truncate">
                    Antwort an <span className="font-semibold">{replyTo.authorName}</span>:{' '}
                    {replyTo.content}
                  </span>
                  <button
                    type="button"
                    onClick={() => setReplyTo(null)}
                    className="text-zinc-400 hover:text-white shrink-0"
                    aria-label="Antwort abbrechen"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <Textarea
                placeholder={
                  replyTo ? 'Schreibe deine Antwort …' : 'Schreibe einen Kommentar …'
                }
                value={commentText}
                onValueChange={setCommentText}
                maxLength={500}
                minRows={2}
                variant="bordered"
                classNames={{
                  inputWrapper:
                    'bg-zinc-950 border-zinc-700 data-[hover=true]:border-zinc-600 group-data-[focus=true]:border-[#3fced1]',
                  input: 'text-white placeholder:text-gray-500',
                }}
              />
              {commentError && <p className="mt-2 text-xs text-red-400">{commentError}</p>}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-zinc-500">{commentText.length} / 500</span>
                <Button
                  size="sm"
                  className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                  startContent={<Send size={14} />}
                  isLoading={isSubmitting}
                  isDisabled={!commentText.trim()}
                  onPress={submitComment}
                >
                  {replyTo ? 'Antworten' : 'Kommentieren'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4 mb-6 text-sm text-zinc-400">
              <RouterLink to="/login" className="text-[#3fced1] hover:text-[#35b5b8] font-medium">
                Melde dich an
              </RouterLink>
              , um einen Kommentar zu schreiben.
            </div>
          )}

          {/* Kommentar-Liste */}
          {loadingComments ? (
            <div className="flex justify-center py-10">
              <Spinner color="primary" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 text-sm">
              Noch keine Kommentare. Sei der Erste!
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {comments.map((comment) => {
                const replies = extraReplies[comment.id] ?? comment.replies ?? [];
                const hasMoreReplies =
                  !extraReplies[comment.id] && comment.replyCount > replies.length;
                return (
                  <div key={comment.id}>
                    <CommentCard
                      comment={comment}
                      canDelete={!!user?.id && comment.authorId === user.id}
                      isDeleting={deletingCommentId === comment.id}
                      onDelete={() => deleteComment(comment)}
                      onReply={
                        isAuthenticated
                          ? () => {
                              setReplyTo(comment);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                          : undefined
                      }
                    />
                    {(replies.length > 0 || hasMoreReplies) && (
                      <div className="ml-6 sm:ml-10 mt-2 flex flex-col gap-2 border-l border-zinc-800 pl-4">
                        {replies.map((reply) => (
                          <CommentCard
                            key={reply.id}
                            comment={reply}
                            isReply
                            canDelete={!!user?.id && reply.authorId === user.id}
                            isDeleting={deletingCommentId === reply.id}
                            onDelete={() => deleteComment(reply)}
                            onReply={
                              isAuthenticated
                                ? () => {
                                    setReplyTo(reply);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }
                                : undefined
                            }
                          />
                        ))}
                        {hasMoreReplies && (
                          <button
                            type="button"
                            onClick={() => loadMoreReplies(comment)}
                            disabled={loadingRepliesFor === comment.id}
                            className="self-start inline-flex items-center gap-1 text-xs text-[#3fced1] hover:text-[#35b5b8] py-1 disabled:opacity-60"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                            {loadingRepliesFor === comment.id
                              ? 'Antworten werden geladen …'
                              : `Alle ${comment.replyCount} Antworten anzeigen`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Statische Sterne-Anzeige (unterstützt halbe Sterne via Clipping) */
const StarsDisplay: React.FC<{ value: number; size?: number }> = ({ value, size = 16 }) => {
  const safe = Number.isFinite(value) ? Math.max(0, Math.min(5, value)) : 0;
  return (
    <div className="flex items-center gap-0.5" aria-label={`${safe.toFixed(1)} von 5 Sternen`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const fillPercent = Math.max(0, Math.min(1, safe - (n - 1))) * 100;
        return (
          <span key={n} className="relative inline-flex" style={{ width: size, height: size }}>
            <Star size={size} className="text-zinc-600 absolute inset-0" />
            <span
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${fillPercent}%` }}
            >
              <Star size={size} className="text-amber-400 fill-amber-400" />
            </span>
          </span>
        );
      })}
    </div>
  );
};

const RatingRow: React.FC<{ rating: SetupRating }> = ({ rating }) => {
  const avatarUrl = communityService.buildThumbnailUrl(rating.userImage, 100);
  const name = rating.userName || 'Unbekannt';
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 sm:p-4">
      <div className="flex items-start gap-3">
        <Avatar
          src={avatarUrl ?? undefined}
          showFallback
          fallback={<UserIcon size={14} className="text-zinc-500" />}
          className="bg-zinc-800 shrink-0 h-8 w-8"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {rating.userName ? (
              <RouterLink
                to={`/spieler/${encodeURIComponent(rating.userName)}`}
                className="text-sm font-semibold text-white hover:text-[#3fced1]"
              >
                {name}
              </RouterLink>
            ) : (
              <span className="text-sm font-semibold text-white">{name}</span>
            )}
            <StarsDisplay value={rating.stars} size={13} />
            <span className="text-[11px] text-zinc-500">
              {communityService.formatRelativeTime(rating.createdAt ?? undefined)}
            </span>
          </div>
          {rating.comment && (
            <p className="mt-1 text-sm text-zinc-300 whitespace-pre-wrap break-words">
              {rating.comment}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const CommentCard: React.FC<{
  comment: SetupComment;
  isReply?: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onDelete: () => void;
  onReply?: () => void;
}> = ({ comment, isReply = false, canDelete, isDeleting, onDelete, onReply }) => {
  const avatarUrl = communityService.buildThumbnailUrl(comment.authorAvatarUrl, 100);
  return (
    <div
      className={`rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 ${
        isReply ? '' : 'sm:p-4'
      }`}
    >
      <div className="flex items-start gap-3">
        <Avatar
          src={avatarUrl ?? undefined}
          showFallback
          fallback={<UserIcon size={14} className="text-zinc-500" />}
          className={`bg-zinc-800 shrink-0 ${isReply ? 'h-7 w-7' : 'h-8 w-8'}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <RouterLink
              to={`/spieler/${encodeURIComponent(comment.authorName)}`}
              className="text-sm font-semibold text-white hover:text-[#3fced1]"
            >
              {comment.authorName}
            </RouterLink>
            <span className="text-[11px] text-zinc-500">
              {communityService.formatRelativeTime(comment.createdAt)}
            </span>
          </div>
          <p className="mt-1 text-sm text-zinc-300 whitespace-pre-wrap break-words">
            {comment.content}
          </p>
          <div className="mt-2 flex items-center gap-3">
            {onReply && (
              <button
                type="button"
                onClick={onReply}
                className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-[#3fced1]"
              >
                <Reply className="w-3.5 h-3.5" />
                Antworten
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={onDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-red-400 disabled:opacity-60"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {isDeleting ? 'Wird gelöscht …' : 'Löschen'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupDetailPage;
